import { useWorkspaceText } from './interface-copy';
import { useEffect, useState } from 'react';
import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import { localizeWorkspaceError } from '@/lib/workspace-error-copy';
import { useWorkspace } from './context';
import { workspaceApi } from './api';
import { dateLabel } from './primitives';
import type { CompanySkill } from './types';
import './skill-archive.css';

export type ArchivedSkill = { id: string; name: string; archivedAt: string; restoreUntil: string };
const DELETE_SKILL_FALLBACK = '__delete_skill_fallback__';
const RESTORE_SKILL_FALLBACK = '__restore_skill_fallback__';

export function DeleteSkill({ skill, disabled }: { skill: CompanySkill; disabled: boolean }) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const locale = useChatInterfaceLocale();
  const ws = useWorkspace();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  if (!skill.canDelete) return null;
  async function remove() {
    const current = ws.beginNavigation();
    setBusy(true);
    setError('');
    try {
      await workspaceApi.deleteSkill(skill.id);
      await ws.refresh();
      if (!current()) return;
      if (ws.turnContext.intent.skillId === skill.id)
        ws.setTurnContext({ ...ws.turnContext, intent: { mode: 'chat' }, selection: undefined });
      ws.openCanvas(null);
      ws.notify(uiText('Skill deleted. You can restore it from the Skills archive for 30 days.'));
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : DELETE_SKILL_FALLBACK);
    } finally {
      setBusy(false);
    }
  }
  return (
    <details className="ws-skill-archive" data-skill-delete>
      <summary>{uiText('More actions')}</summary>
      {confirming ? (
        <div>
          <p>
            {uiRich(
              'Delete “{value1}”? It will disappear from Skills and search, and cannot be used by agents. You can restore it for 30 days. Existing chats and results stay available.',
              { value1: skill.draft.name },
            )}
          </p>
          <div className="ws-actions">
            <button className="ws-button" disabled={busy} onClick={() => setConfirming(false)}>
              {uiText('Cancel')}
            </button>
            <button className="ws-button" disabled={busy || disabled} onClick={() => void remove()}>
              {busy ? uiText('Deleting…') : uiText('Confirm delete')}
            </button>
          </div>
        </div>
      ) : (
        <button className="ws-text-button" disabled={disabled} onClick={() => setConfirming(true)}>
          {uiText('Delete Skill')}
        </button>
      )}
      {error && (
        <p className="ws-error" role="alert">
          {error === DELETE_SKILL_FALLBACK
            ? uiText('Could not delete this Skill.')
            : localizeWorkspaceError(error, locale)}
        </p>
      )}
    </details>
  );
}

// The parent keys this component by company/user. Recovery stays out of the
// normal library and requests no archived metadata until explicitly opened.
export function SkillArchive() {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const locale = useChatInterfaceLocale();
  const ws = useWorkspace();
  const [open, setOpen] = useState(false);
  const [skills, setSkills] = useState<ArchivedSkill[]>([]);
  const [offset, setOffset] = useState(0);
  const [next, setNext] = useState<number | null>(null);
  const [tick, setTick] = useState(0);
  const skillListKey = ws.data.skills.map((skill) => skill.id).join(',');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    setOffset(0);
    setSkills([]);
    setTick((value) => value + 1);
  }, [skillListKey]);
  useEffect(() => {
    if (!open) return;
    let active = true;
    setBusy(true);
    setError('');
    void workspaceApi
      .archivedSkills(offset)
      .then((page) => {
        if (!active) return;
        setSkills((previous) => (offset ? [...previous, ...page.skills] : page.skills));
        setNext(page.nextOffset);
      })
      .catch((failure) => {
        if (active) setError(failure.message);
      })
      .finally(() => {
        if (active) setBusy(false);
      });
    return () => {
      active = false;
    };
  }, [open, offset, tick]);
  async function restore(id: string) {
    setBusy(true);
    setError('');
    try {
      await workspaceApi.restoreArchivedSkill(id);
      setOffset(0);
      setSkills([]);
      setTick((value) => value + 1);
      await ws.refresh();
      ws.notify(
        uiText(
          'Skill restored as an unpublished draft. Its author can review and publish it again.',
        ),
      );
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : RESTORE_SKILL_FALLBACK);
    } finally {
      setBusy(false);
    }
  }
  return (
    <details
      className="ws-skill-archive"
      onToggle={(event) => {
        setOpen(event.currentTarget.open);
        setOffset(0);
      }}
    >
      <summary>{uiText('Archived Skills')}</summary>
      {open && (
        <div>
          <p className="ws-page-note">
            {uiText(
              'Deleted Skills can be restored for 30 days. Restoring leaves them unpublished.',
            )}
          </p>
          {skills.map((skill) => (
            <div className="ws-archived-skill" key={skill.id}>
              <div>
                <strong>{skill.name}</strong>
                <p className="ws-muted">
                  {uiRich('Restore until {value1}', {
                    value1: dateLabel(skill.restoreUntil, uiText.locale),
                  })}
                </p>
              </div>
              <button className="ws-button" disabled={busy} onClick={() => void restore(skill.id)}>
                {uiText('Restore')}
              </button>
            </div>
          ))}
          {busy && <p role="status">{uiText('Loading…')}</p>}
          {!busy && !error && !skills.length && (
            <p className="ws-muted">{uiText('No archived Skills.')}</p>
          )}
          {error && (
            <p className="ws-error" role="alert">
              {error === RESTORE_SKILL_FALLBACK
                ? uiText('Could not restore this Skill.')
                : localizeWorkspaceError(error, locale)}{' '}
              <button
                className="ws-text-button"
                disabled={busy}
                onClick={() => {
                  setOffset(0);
                  setTick((value) => value + 1);
                }}
              >
                {uiText('Try again')}
              </button>
            </p>
          )}
          {next !== null && !error && (
            <button className="ws-button" disabled={busy} onClick={() => setOffset(next)}>
              {uiText('Load more archived Skills')}
            </button>
          )}
        </div>
      )}
    </details>
  );
}
